import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment as env } from "../../../../../../environments/environment.prod";
import { saveAs } from 'file-saver';
import { environment } from "environments/environment.qas";
import { InvoicePdfOptions } from './invoice-pdf-options.interface';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';

@Injectable({
    providedIn: 'root'
})
export class InvoiceService {
    private readonly baseUrl = env.API_DOCUMENT_EXPORT_URL;
    constructor(private http: HttpClient) { }

    downloadDocument(id: string, options?: InvoicePdfOptions, typeDocument?: DocumentTypeEnum): Observable<void> {
        switch (typeDocument) {
        case DocumentTypeEnum.DEVOLUCION_DE_PRODUCTO:
            return this.downloadAndSaveRefund(id, options);

        case DocumentTypeEnum.CAMBIO_DE_PRODUCTO:
            return this.downloadAndSaveChangeProduct(id, options);

        case DocumentTypeEnum.ENTREGA_SALIDA:
            return this.downloadAndSaveDelivery(id, options);

        case DocumentTypeEnum.DOCUMENTO_MERMA:
            return this.downloadAndSaveMerma(id, options);

        case DocumentTypeEnum.PEDIDO:
            return this.downloadAndSaveOrder(id, options);

        case DocumentTypeEnum.COTIZACION:
            return this.downloadAndSaveQuote(id, options);

        case DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO:
            return this.downloadAndSaveRequestChangeProduct(id, options);
        
        case DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO:
            return this.downloadAndSaveRequestRefund(id, options);

        default:
            throw new Error(`Tipo de documento no soportado: ${typeDocument}`);
        }
    }


    downloadAndSaveInvoice(invoiceId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/invoice/${invoiceId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Factura", invoiceId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveOrder(orderId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/order/${orderId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Pedido",orderId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveMerma(mermaId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/merma/${mermaId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Merma", mermaId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveChangeProduct(changeProductId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/change-product/${changeProductId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Cambio_Producto", changeProductId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveRefund(refundId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/refund/${refundId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Devolución", refundId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveQuote(quoteId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/quote/${quoteId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Cotización",quoteId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveDelivery(deliveryId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/delivery/${deliveryId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Entrega",deliveryId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveRequestRefund(requestRefundId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/request-refund/${requestRefundId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Solicitud_Devolución",requestRefundId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    downloadAndSaveRequestChangeProduct(requestId: string, options?: InvoicePdfOptions): Observable<void> {
        let params = new HttpParams();
        if (options) {
            Object.entries(options).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            });
        }
        return this.http.get(`${this.baseUrl}/order-pdf/request-change-product/${requestId}`, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => {
                const fileName = this.generateFileName("Solicitud_Cambio_Producto",requestId);
                saveAs(blob, fileName);
            }),
            tap(() => { })
        ) as Observable<void>;
    }

    canReprintInvoice(element: any): boolean {
        return !!element?._id;
    }
    private generateFileName(document: string, invoiceId: string): string {
        const date = new Date().toISOString().split('T')[0];
        return `${document}_${invoiceId}_${date}.pdf`;
    }
}